import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  View,
  StyleSheet,
  ImageBackground,
  FlatList,
  Dimensions,
  TouchableOpacity,
  Pressable,
  Alert,
} from "react-native";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import RefreshIcon from "@assets/svg/RefreshIcon";
import PaymentFailed from "@assets/svg/PaymentFailed";
import PaymentFailedIcon from "@assets/svg/MyTasks/PaymentFailed";
import PaymentPending from "@assets/svg/PaymentPending";
import PaymentPendingPageSvg from "@assets/svg/PaymentPendingPage";
import Tick from "@assets/svg/Tick";
import CustomText from "@components/CustomText";
import LinearGradient from "react-native-linear-gradient";
import {
  createQrCodePayment,
  getQrCodeStatus,
  markMultipleDeliveries,
  sendQrPaymentOnWhatsApp,
} from "@services/api";
import WhatsappDefault from "@assets/svg/WhatsappDefault";
import useCountDown from "react-countdown-hook";
// eslint-disable-next-line import/no-named-as-default
import t from "@constants/localization";
import { useDispatch, useSelector } from "react-redux";
import useLocationHook from "@components/common/LocationHook";
import ToastMessage from "@components/Toast";
import { actions as myTaskActions } from "@reducers/myTaskReducer";
import InstantCommissionModal from "@screens/MyTasks/OtpVerifcation/InstantCommissionModal";
import { buildDeliveryProofPayload } from "@screens/MyTasks/utils";
import color from "@constants/color";

const PATH = "CpCxQrPayment";

const PALETTE = {
  refreshBg: "rgba(231, 242, 255, 1)",
  refreshTextColor: "rgba(38, 133, 248, 1)",
  iconWrapperBg: "#FFFFFF",
  retryButtonBg: "#3A3A3A",
  whatsappButtonBg: "#25D366",
};

const getPaymentHistoryStyles = (status) =>
  status === "FAILED" || status === "EXPIRED"
    ? { Icon: PaymentFailed, color: "rgba(255, 223, 236, 1)" }
    : status === "PAID"
      ? { Icon: Tick, color: "rgba(67, 203, 142, 0.6)" }
      : { Icon: PaymentPending, color: "rgba(33, 149, 242, 0.1)" };

function ItemSeparatorComponent() {
  return <View style={styles.separator} />;
}

function Countdown({ countdownTime }) {
  const minutes = Math.floor(countdownTime / 1000 / 60);
  const seconds = Math.floor(countdownTime / 1000) % 60;
  return (
    <View style={styles.countdownContainer}>
      <CustomText variant="title_16">
        {t(`${PATH}/completePaymentWithin`)}
      </CustomText>
      <CustomText variant="subtitle_16" style={styles.countdownText}>
        {`0${minutes}:${String(seconds).length === 1 ? "0" : ""}${seconds}`}
      </CustomText>
    </View>
  );
}

function RefreshBtn({ onRefresh = () => {} }) {
  return (
    <TouchableOpacity
      testID="refresh_btn"
      onPress={onRefresh}
      style={styles.refreshBtnContainer}
      activeOpacity={0.5}
    >
      <RefreshIcon />
      <CustomText variant="caption_4" style={styles.refreshText}>
        {t(`${PATH}/refresh`)}
      </CustomText>
    </TouchableOpacity>
  );
}

function CPCXQRPayment({ navigation, route }) {
  const { orderMarkingMap } = useSelector((state) => state.myTaskReducer);
  const { width, height } = Dimensions.get("screen");
  const [qrData, setQrData] = useState({});
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [commissionMsgs, setCommissionMsgs] = useState("");
  const [showInstantCommissionModal, setShowInstantCommissionModal] =
    useState(false);
  const [countDownTime, { start }] = useCountDown(2 * 60 * 1000, 1000);
  const [qrPaymentStatus, setQrPaymentStatus] = useState({
    is_paid: false,
    status: "",
    qr_order_id: "",
    is_active: false,
  });
  const [whatsappSending, setWhatsappSending] = useState(false);
  const [whatsappSent, setWhatsappSent] = useState(false);
  const {
    cx_id = "",
    payable_amount = 0,
    otp_enabled = false,
    payout_plan = {},
    cx_phone = "",
    plastic_bag = null,
    delivery_proof = null,
  } = route.params;
  const isProcessingRef = useRef(false);
  const getCurrentLocation = useLocationHook();
  const dispatch = useDispatch();
  const { pollingEnable = false, pollingInterval = 30 } = useSelector(
    ({ configReducer }) => configReducer,
  );
  const getQrData = useCallback(async () => {
    try {
      const { data } = await createQrCodePayment(
        orderMarkingMap,
        cx_id,
        payable_amount,
      );
      setQrData(data);
      setPaymentHistory(data?.payment_history || []);
      if (data?.time_left !== null) {
        start(data.time_left * 1000);
      }
    } catch {
      ToastMessage("error", t(`${PATH}/qrGenerationFailed`));
      navigation.goBack();
    }
  }, [orderMarkingMap, cx_id, payable_amount, start, navigation]);

  const doSendWhatsApp = useCallback(async () => {
    if (!qrData.qr_order_id || !cx_phone) return;
    setWhatsappSending(true);
    try {
      await sendQrPaymentOnWhatsApp(qrData.qr_order_id, cx_phone);
      setWhatsappSent(true);
      ToastMessage("success", t(`${PATH}/whatsappSentSuccess`));
    } catch {
      ToastMessage("error", t(`${PATH}/whatsappSentFailed`));
    } finally {
      setWhatsappSending(false);
    }
  }, [qrData.qr_order_id, cx_phone]);

  const handleSendWhatsApp = useCallback(() => {
    if (whatsappSent) {
      Alert.alert(
        t(`${PATH}/sendOnWhatsApp`),
        t(`${PATH}/whatsappAlreadySent`),
        [
          { text: t(`${PATH}/resend`), onPress: doSendWhatsApp },
          { text: t("common/ok"), style: "cancel" },
        ],
      );
    } else {
      doSendWhatsApp();
    }
  }, [whatsappSent, doSendWhatsApp]);

  const getQrStatus = useCallback(
    async (qr_order_id) => {
      try {
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;
        const { data } = await getQrCodeStatus({
          qrOrderId: qr_order_id,
          otp_enabled: otp_enabled,
        });
        setPaymentHistory(data?.payment_history || []);
        setQrPaymentStatus(data);
        if (data?.is_paid) {
          const coords = await getCurrentLocation(2);
          const payload = {
            orders: Object.values(orderMarkingMap),
            otp: "",
            lat: coords.latitude,
            lng: coords.longitude,
            otp_enabled,
            online_payment_enabled: true,
            collectible_amount: parseFloat(payable_amount),
            cx_user_id: cx_id,
            status: data?.status,
            amount: data?.amount,
            paytm_order_id: data?.paytm_order_id,
            payment_mode: "PREPAID",
            payout_plan,
            ...buildDeliveryProofPayload({
              plasticBag: plastic_bag,
              deliveryProof: delivery_proof,
            }),
          };
          const response = await markMultipleDeliveries(payload);
          if (response?.data?.payload?.error === "OTP_VERIFICATION_FAILED") {
            return navigation.replace("NewOTPVerification", {
              otpLength: response?.data?.payload?.otpLength || 4,
              otpText: response?.data?.payload?.otpText || "",
              online_payment_enabled: true,
              collectible_amount: payable_amount,
              cx_user_id: cx_id,
              paymentMode: "PREPAID",
              phone_numbers: response?.data?.payload?.phone_numbers || [],
              masked_phone_numbers:
                response?.data?.payload?.masked_phone_numbers ||
                response?.data?.payload?.phone_numbers ||
                [],
              extra_info: response?.data?.payload?.extra_info || "",
              status: data?.status,
              amount: data?.amount,
              paytm_order_id: data?.paytm_order_id,
              payout_plan,
              enable_mark_without_otp:
                response?.data?.payload?.enable_mark_without_otp,
              cancel_del_incentive_msg:
                response?.data?.payload?.cancel_del_incentive_msg,
              send_cancel_delivery_otp:
                response?.data?.payload?.send_cancel_delivery_otp || false,
              plastic_bag: plastic_bag || undefined,
              delivery_proof: delivery_proof || undefined,
            });
          }
          if (response.status === 200) {
            dispatch(myTaskActions.clearOrderMarking());
            if (
              response?.data?.payload?.type === "INSTANT_COMMISSION_TRIGGERED"
            ) {
              setCommissionMsgs(response?.data?.payload);
              setShowInstantCommissionModal(true);
              return;
            }
            ToastMessage("success", response?.data?.payload?.toastMessage);
            navigation.pop(2);
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      } finally {
        isProcessingRef.current = false;
      }
    },
    [
      cx_id,
      delivery_proof,
      dispatch,
      getCurrentLocation,
      navigation,
      orderMarkingMap,
      otp_enabled,
      payable_amount,
      payout_plan,
      plastic_bag,
    ],
  );

  const renderItem = ({ item }) => {
    const { Icon, color: itemColor } = getPaymentHistoryStyles(item.status);
    return (
      <View>
        <View style={styles.itemContainer}>
          <View style={[styles.itemCircle, { backgroundColor: itemColor }]}>
            <Icon strokeColor="white" />
          </View>
          <View style={styles.itemTextContainer}>
            <CustomText variant="body_text_4">
              {item.status_txt || "PENDING"}
            </CustomText>
            <CustomText variant="caption_2" style={styles.statusText}>
              {item.created_at_txt || "today"}
            </CustomText>
          </View>
          <View style={styles.amountTextContainer}>
            <CustomText
              variant="subtitle_16"
              style={[styles.amountText, { color: item.amount_txt_color }]}
            >
              {item.amount_txt || ""}
            </CustomText>
          </View>
        </View>
        {item.response_message ? (
          <View
            style={[styles.reasonContainer, { backgroundColor: itemColor }]}
          >
            <CustomText variant="caption_2" style={styles.reasonText}>
              {item.response_message}
            </CustomText>
          </View>
        ) : null}
      </View>
    );
  };

  const getHeaderComponent = ({ shouldShowRetryBtn }) => {
    return (
      <HeaderWithBackAndTitle
        headertitle={t(`${PATH}/payment`)}
        headerColor="#fff"
        hideShadow={false}
        navigation={navigation}
        route={route}
        rightComponent={
          shouldShowRetryBtn ? (
            <RefreshBtn onRefresh={() => getQrStatus(qrData.qr_order_id)} />
          ) : (
            <></>
          )
        }
      />
    );
  };

  const getFooterComponent = () => {
    if (qrPaymentStatus.status === "PENDING") {
      return (
        <View
          style={[
            styles.mainContainer,
            styles.pendingContainer,
            { minHeight: height * 0.9 },
          ]}
        >
          <PaymentPendingPageSvg />
          <CustomText variant="title_16" style={styles.pendingText}>
            {t(`${PATH}/paymentPending`, {
              amount: qrData.amount_txt,
            })}
          </CustomText>
        </View>
      );
    } else if (qrPaymentStatus.status === "FAILED") {
      return (
        <View
          style={[styles.failedStateContainer, { minHeight: height * 0.9 }]}
        >
          <LinearGradient
            style={styles.failureCard}
            colors={["#FF4D4F", "#FF9495"]}
            start={{ x: 0, y: 1 }}
            end={{ x: 1, y: 0 }}
            angle={296.64}
            useAngle={true}
          >
            <View style={styles.iconWrapper}>
              <PaymentFailedIcon width={40} height={40} color="#FF5C5C" />
            </View>
            <View style={styles.failureContent}>
              <CustomText variant="title_18_semi" style={styles.failureTitle}>
                Payment Failed
              </CustomText>
              <CustomText variant="body_16" style={styles.failureMessage}>
                {t(`${PATH}/paymentNotReceived`, {
                  amount: qrData.amount_txt,
                })}
              </CustomText>
            </View>
          </LinearGradient>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={() => navigation.goBack()}
          >
            <CustomText variant="title_16" style={styles.retryButtonText}>
              {t(`${PATH}/tryAgain`)}
            </CustomText>
          </TouchableOpacity>
        </View>
      );
    } else {
      return (
        <View style={styles.mainContainer}>
          <LinearGradient
            style={styles.qrAmountContainer}
            angle={270}
            useAngle={true}
            colors={["rgba(18,18,18,1)", "rgba(99, 99, 99, 1)"]}
          >
            <View style={styles.amountContainer}>
              <CustomText style={styles.whiteText} variant="subtitle_16">
                {t(`${PATH}/amountToPay`)}
              </CustomText>
              <CustomText
                style={[styles.whiteText, styles.payment]}
                variant="title_18_semi"
              >
                {qrData.amount_txt || ""}
              </CustomText>
            </View>
            <View style={styles.qrImageContainer}>
              <ImageBackground
                source={{ uri: qrData.qrImage }}
                style={[
                  styles.qrImage,
                  { width: width * 0.75, height: width * 0.75 },
                ]}
              />
            </View>
          </LinearGradient>
          {qrData.show_whatsapp_btn && cx_phone ? (
            <Pressable
              testID="send_whatsapp_btn"
              onPress={handleSendWhatsApp}
              disabled={whatsappSending}
              style={({ pressed }) => [
                styles.whatsappButton,
                pressed && styles.whatsappButtonPressed,
                whatsappSending && styles.whatsappButtonDisabled,
              ]}
            >
              <WhatsappDefault size={20} />
              <CustomText variant="title_16" style={styles.whatsappButtonText}>
                {whatsappSending ? "..." : t(`${PATH}/sendOnWhatsApp`)}
              </CustomText>
            </Pressable>
          ) : null}
          <Countdown countdownTime={countDownTime} />
          <View style={styles.paymentHistoryContainer}>
            {paymentHistory.length > 0 ? (
              <FlatList
                data={paymentHistory}
                renderItem={renderItem}
                ListHeaderComponent={
                  <CustomText variant="title_16" style={styles.historyTitle}>
                    {t(`${PATH}/paymentHistory`)}
                  </CustomText>
                }
                keyExtractor={(item) => item.id}
                ItemSeparatorComponent={ItemSeparatorComponent}
              />
            ) : null}
          </View>
          {showInstantCommissionModal && (
            <InstantCommissionModal
              isVisible={showInstantCommissionModal}
              onClose={() => {
                navigation.pop(2);
              }}
              commissionMsgs={commissionMsgs}
            />
          )}
        </View>
      );
    }
  };

  useEffect(() => {
    getQrData();
    start();
  }, [getQrData, start]);

  useEffect(() => {
    if (!pollingEnable) return;
    let intervalId = null;
    let isMounted = true;

    const checkStatus = async () => {
      if (!isMounted) return;
      await getQrStatus(qrData.qr_order_id);
    };

    if (qrData.qr_order_id && !qrPaymentStatus.is_paid) {
      checkStatus();
      intervalId = setInterval(checkStatus, pollingInterval * 1000);
    }

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    qrData.qr_order_id,
    qrPaymentStatus.is_paid,
    getQrStatus,
    pollingInterval,
    pollingEnable,
  ]);

  const shouldShowRetryBtn = !(
    qrPaymentStatus.status === "FAILED" || qrPaymentStatus.status === "PENDING"
  );

  return (
    <View style={styles.container}>
      {getHeaderComponent({ shouldShowRetryBtn })}
      <FlatList
        style={styles.flatList}
        ListFooterComponent={getFooterComponent()}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  refreshBtnContainer: {
    padding: 8,
    backgroundColor: PALETTE.refreshBg,
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    borderRadius: 6,
  },
  qrAmountContainer: {
    backgroundColor: color.black,
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 10,
  },
  amountContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  qrImageContainer: {
    backgroundColor: color.white,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 10,
  },
  paymentHistoryContainer: {
    marginTop: 10,
  },
  itemCircle: {
    height: 45,
    width: 45,
    backgroundColor: color.plain_red,
    borderRadius: 25,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  itemContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    marginVertical: 8,
  },
  countdownContainer: {
    marginTop: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  itemTextContainer: {
    flex: 1,
    marginLeft: 10,
  },
  separator: {
    height: 1,
    backgroundColor: color.lighterBlack,
  },
  countdownText: {
    marginLeft: 5,
    color: color.plain_red,
  },
  refreshText: {
    marginLeft: 4,
    color: PALETTE.refreshTextColor,
  },
  statusText: {
    color: color.black06,
  },
  amountText: {
    color: color.mediumBlack,
  },
  pendingText: {
    marginTop: 10,
  },
  whiteText: {
    color: color.white,
  },
  historyTitle: {
    marginBottom: 6,
  },
  container: {
    flex: 1,
  },
  flatList: {
    flex: 1,
  },
  mainContainer: {
    flex: 1,
    backgroundColor: color.white,
    paddingHorizontal: 20,
    paddingVertical: 15,
  },
  pendingContainer: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 0,
  },
  qrImage: {
    borderRadius: 10,
  },
  payment: {
    color: color.white,
    fontSize: 36,
    textAlign: "center",
    fontWeight: "bold",
    lineHeight: 40,
  },
  reasonContainer: {
    flex: 1,
    justifyContent: "center",
    borderRadius: 6,
    padding: 10,
    paddingVertical: 8,
    marginBottom: 10,
  },
  reasonText: {
    color: color.mediumBlack,
  },
  failedStateContainer: {
    flex: 1,
    paddingTop: 30,
    backgroundColor: color.white,
    paddingHorizontal: 16,
  },
  failureCard: {
    borderRadius: 16,
    width: "100%",
    paddingTop: 40,
    paddingBottom: 32,
    alignItems: "center",
  },
  iconWrapper: {
    backgroundColor: PALETTE.iconWrapperBg,
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  failureContent: {
    alignItems: "center",
    paddingHorizontal: 24,
    marginTop: 16,
  },
  failureTitle: {
    color: color.white,
    marginBottom: 12,
    fontSize: 20,
    lineHeight: 24,
  },
  failureMessage: {
    color: color.white,
    textAlign: "center",
    fontSize: 16,
    lineHeight: 20,
  },
  retryButton: {
    backgroundColor: PALETTE.retryButtonBg,
    paddingVertical: 14,
    borderRadius: 8,
    width: "50%",
    marginTop: 24,
    alignSelf: "center",
    alignItems: "center",
  },
  retryButtonText: {
    color: color.white,
    fontSize: 16,
    lineHeight: 20,
    fontWeight: "600",
  },
  whatsappButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: PALETTE.whatsappButtonBg,
    borderRadius: 8,
    paddingVertical: 12,
    marginTop: 12,
    gap: 8,
  },
  whatsappButtonPressed: {
    opacity: 0.85,
  },
  whatsappButtonDisabled: {
    opacity: 0.55,
  },
  whatsappButtonText: {
    color: color.white,
    marginLeft: 8,
  },
});

export default CPCXQRPayment;
